import * as React from 'react'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'

import type { BestFitResponse } from '@user/store/scheduling'
import Link from '@mui/material/Link'
import LaunchIcon from '@mui/icons-material/Launch'
import { Theme } from '@mui/material/styles'

import { useBestFitResult } from './hooks'
import type { CommitHandler } from '../typings'
import { AlternateTime } from './AlternateTime'
import { BestFitResultCardLayout as Layout } from '../atoms'
import { useLocalizationDateFormat } from '@user/features/Localization/useLocalizationDateFormat'
import Box from '@mui/material/Box/Box'

export type AppointmentSlotProps = {
  result: BestFitResponse
  commitResult: CommitHandler
}

export function AppointmentSlot(props: AppointmentSlotProps) {
  const { commitResult } = props
  const { formatDate } = useLocalizationDateFormat()
  const {
    name,
    date,
    dateText,
    time,
    miles,
    resultId,
    driveTime,
    buttonLabel,
    scheduleLink,
    onClickResult,
    adtlTimesOpen,
    adtlTimesLabel,
    commitProcessing,
    onClickAdtlTimes,
    formattedAltTimes,
    showAdtlTimesLink,
  } = useBestFitResult(props)

  return (
    <Box sx={{ backgroundColor: '#F9FAFB', mb: 1.2 }}>
      <Layout.Body>
        <Layout.Left>
          <Typography variant="h6">
            {dateText} {formatDate(date)}
          </Typography>
          <Typography variant="body2">{name}</Typography>
          <Typography variant="body2">{miles} miles away</Typography>
          {!!scheduleLink && (
            <Link href={scheduleLink} target="_blank" rel="noopener noreferrer">
              <Button variant="text">
                View Schedule
                <LaunchIcon
                  sx={{
                    marginLeft: (theme: Theme) => theme.spacing(0.5),
                    fontSize: '20px',
                  }}
                />
              </Button>
            </Link>
          )}
        </Layout.Left>

        <Layout.Right>
          <Button
            variant="contained"
            onClick={onClickResult}
            disabled={commitProcessing}
            data-test-id={`results-commit-${resultId}`}
          >
            {buttonLabel}
          </Button>
          {!!time && <Typography variant="body2">{time}</Typography>}
          <Typography variant="body2">
            Additional Drive Time: {driveTime}
          </Typography>
          {showAdtlTimesLink && (
            <Button variant="text" onClick={onClickAdtlTimes} href="">
              {adtlTimesLabel}
            </Button>
          )}
        </Layout.Right>
      </Layout.Body>

      {adtlTimesOpen && (
        <Layout.Times>
          {formattedAltTimes.map((result) => (
            <AlternateTime
              key={result.resultId}
              result={result}
              commitResult={commitResult}
              commitProcessing={commitProcessing}
            />
          ))}
        </Layout.Times>
      )}
    </Box>
  )
}
